import { Button } from '@/components';
import Markdown from 'react-markdown';
import { ReactNode, useState } from 'react';
import { copyMessage, rateMessage } from '../lib/logic';
import { appNotify } from '@/lib/appNotify';
import { ChatMessage } from '../types';

// Custom components for markdown elements
const markdownComponents = {
    // Paragraphs
    p: ({ children, ...props }: { children?: ReactNode; [key: string]: any }) => (
        <p className='mb-3 text-sm leading-relaxed last:mb-0' {...props}>
            {children}
        </p>
    ),
    // Links
    a: ({ children, href, ...props }: { children?: ReactNode; href?: string; [key: string]: any }) => (
        <a
            href={href}
            className='text-blue-500 underline decoration-1 underline-offset-2 hover:text-blue-600'
            target='_blank'
            rel='noopener noreferrer'
            {...props}
        >
            {children}
        </a>
    ),
    // Headings
    h1: ({ children, ...props }: { children?: ReactNode; [key: string]: any }) => (
        <h1 className='mt-4 mb-3 text-lg font-bold first:mt-0' {...props}>
            {children}
        </h1>
    ),
    h2: ({ children, ...props }: { children?: ReactNode; [key: string]: any }) => (
        <h2 className='mt-3 mb-2 text-base font-semibold first:mt-0' {...props}>
            {children}
        </h2>
    ),
    h3: ({ children, ...props }: { children?: ReactNode; [key: string]: any }) => (
        <h3 className='mt-3 mb-2 text-sm font-semibold first:mt-0' {...props}>
            {children}
        </h3>
    ),
    // Lists
    ul: ({ children, ...props }: { children?: ReactNode; [key: string]: any }) => (
        <ul className='mb-2 ml-4 list-outside list-disc space-y-1' {...props}>
            {children}
        </ul>
    ),
    ol: ({ children, ...props }: { children?: ReactNode; [key: string]: any }) => (
        <ol className='mb-2 ml-4 list-outside list-decimal space-y-1' {...props}>
            {children}
        </ol>
    ),
    li: ({ children, ...props }: { children?: ReactNode; [key: string]: any }) => (
        <li className='text-sm leading-relaxed' {...props}>
            {children}
        </li>
    ),
    // Code component - handles inline code only
    code: ({ children, ...props }: { children?: ReactNode; [key: string]: any }) => {
        // Always render as inline code
        // Block code will be handled by the pre > code combination
        return (
            <code className='rounded bg-slate-900/40 px-1.5 py-0.5 font-mono text-xs text-amber-600' {...props}>
                {children}
            </code>
        );
    },
    // Pre elements (code blocks - triple backticks)
    pre: ({ children, ...props }: { children?: ReactNode; [key: string]: any }) => {
        return (
            <pre
                className='my-4 overflow-x-auto rounded-md bg-slate-200/30 p-3 font-mono text-xs dark:bg-slate-900/40 [&>code]:rounded-none [&>code]:bg-transparent [&>code]:p-0 [&>code]:text-inherit'
                {...props}
            >
                {children}
            </pre>
        );
    },
    // Blockquotes
    blockquote: ({ children, ...props }: { children?: ReactNode; [key: string]: any }) => (
        <blockquote
            className='my-2 border-l-4 border-slate-300 pl-4 text-slate-600 italic dark:border-slate-600 dark:text-slate-400'
            {...props}
        >
            {children}
        </blockquote>
    ),
    // Strong/Bold
    strong: ({ children, ...props }: { children?: ReactNode; [key: string]: any }) => (
        <strong className='font-semibold' {...props}>
            {children}
        </strong>
    ),
    // Emphasis/Italic
    em: ({ children, ...props }: { children?: ReactNode; [key: string]: any }) => (
        <em className='italic' {...props}>
            {children}
        </em>
    ),
};

export function AssistantChatBlob({ message, handleResubmit }: { message: ChatMessage; handleResubmit: () => void }) {
    const [copiedMessageIndex, setCopiedMessageIndex] = useState<string | null>(null);
    const [messageRating, setMessageRating] = useState<number | null>(message.rating ?? null);
    const handleCopy = (index: string | null) => {
        setCopiedMessageIndex(index);
    };

    const handleRate = (messageId: string, rating: number) => {
        const previousRating = messageRating;
        setMessageRating(rating);

        rateMessage(messageId, message.content, rating)
            .then(() => {
                // Success - UI already updated, nothing to do
            })
            .catch(() => {
                setMessageRating(previousRating);
                appNotify({
                    type: 'error',
                    text: 'Error rating message. Please try again later.',
                });
            });
    };
    return (
        <div className='flex justify-start'>
            <div className={`max-w-xs rounded-2xl px-4 py-2 lg:max-w-md ${'bg-slate-200/20'} `}>
                <div className='no-scrollbar overflow-auto'>
                    <Markdown components={markdownComponents}>{message.content}</Markdown>
                    {message.isStreaming && (
                        <span className='mb-[-2px] ml-1 inline-block h-4 w-1.5 animate-pulse bg-current' />
                    )}
                </div>
                <div className='flex justify-end'>
                    <div className='text-xs text-slate-400'>
                        {!message.isStreaming && message.content && (
                            <div className='message-actions'>
                                <Button
                                    className={
                                        'highlight-menu-item cursor-pointer rounded-md border-none bg-none opacity-50 hover:opacity-100' +
                                        (messageRating === 1 ? ' bg-slate-400/10 text-white/90 opacity-100' : '')
                                    }
                                    onClick={() => (message.id ? handleRate(message.id, 1) : null)}
                                    title='Helpful'
                                >
                                    <svg width='20' height='20' viewBox='0 0 20 20' fill='currentColor'>
                                        <path d='M10.9153 1.83987L11.2942 1.88772L11.4749 1.91507C13.2633 2.24201 14.4107 4.01717 13.9749 5.78225L13.9261 5.95901L13.3987 7.6719C13.7708 7.67575 14.0961 7.68389 14.3792 7.70608C14.8737 7.74486 15.3109 7.82759 15.7015 8.03323L15.8528 8.11819C16.5966 8.56353 17.1278 9.29625 17.3167 10.1475L17.347 10.3096C17.403 10.69 17.3647 11.0832 17.2835 11.5098C17.2375 11.7517 17.1735 12.0212 17.096 12.3233L16.8255 13.3321L16.4456 14.7276C16.2076 15.6001 16.0438 16.2356 15.7366 16.7305L15.595 16.9346C15.2989 17.318 14.9197 17.628 14.4866 17.8408L14.2982 17.9258C13.6885 18.1774 12.9785 18.1651 11.9446 18.1651H7.33331C6.64422 18.1651 6.08726 18.1657 5.63702 18.1289C5.23638 18.0962 4.87565 18.031 4.53936 17.8867L4.39679 17.8203C3.87576 17.5549 3.43916 17.151 3.13507 16.6553L3.013 16.4366C2.82119 16.0599 2.74182 15.6541 2.7044 15.1963C2.66762 14.7461 2.66827 14.1891 2.66827 13.5V11.667C2.66827 10.9349 2.66214 10.4375 2.77569 10.0137L2.83722 9.81253C3.17599 8.81768 3.99001 8.05084 5.01397 7.77639L5.17706 7.73928C5.56592 7.66435 6.02595 7.66799 6.66632 7.66799C6.9429 7.66799 7.19894 7.52038 7.33624 7.2803L10.2562 2.16995L10.3118 2.08792C10.4544 1.90739 10.6824 1.81092 10.9153 1.83987ZM7.33136 14.167C7.33136 14.9841 7.33714 15.2627 7.39386 15.4746L7.42999 15.5918C7.62644 16.1686 8.09802 16.6134 8.69171 16.7725L8.87042 16.8067C9.07652 16.8323 9.38687 16.835 10.0003 16.835H11.9446C13.099 16.835 13.4838 16.8228 13.7903 16.6963L13.8997 16.6465C14.1508 16.5231 14.3716 16.3444 14.5433 16.1221L14.6155 16.0166C14.7769 15.7552 14.8968 15.3517 15.1624 14.378L15.5433 12.9824L15.8079 11.9922C15.8804 11.7102 15.9368 11.4711 15.9769 11.2608C16.0364 10.948 16.0517 10.7375 16.0394 10.5791L16.0179 10.4356C15.9156 9.97497 15.641 9.57381 15.2542 9.31253L15.0814 9.20999C14.9253 9.12785 14.6982 9.06544 14.2747 9.03225C13.8477 8.99881 13.2923 8.99807 12.5003 8.99807C12.2893 8.99807 12.0905 8.89822 11.9651 8.72854C11.8398 8.55879 11.8025 8.33942 11.8646 8.13772L12.6556 5.56741L12.7054 5.36331C12.8941 4.35953 12.216 3.37956 11.1878 3.2178L8.49054 7.93948C8.23033 8.39484 7.81431 8.72848 7.33136 8.88967V14.167ZM3.99835 13.5C3.99835 14.2111 3.99924 14.7044 4.03058 15.0879C4.06128 15.4636 4.11804 15.675 4.19854 15.833L4.26886 15.959C4.44517 16.2466 4.69805 16.4808 5.0003 16.6348L5.13019 16.6905C5.27397 16.7419 5.46337 16.7797 5.74542 16.8028C5.97772 16.8217 6.25037 16.828 6.58722 16.8311C6.41249 16.585 6.27075 16.3136 6.1712 16.0215L6.10968 15.8194C5.99614 15.3956 6.00128 14.899 6.00128 14.167V9.00296C5.79386 9.0067 5.65011 9.01339 5.53741 9.02737L5.3587 9.06057C4.76502 9.21965 4.29247 9.66448 4.09601 10.2412L4.06085 10.3584C4.00404 10.5705 3.99835 10.8493 3.99835 11.667V13.5Z' />
                                    </svg>
                                </Button>

                                <Button
                                    className={
                                        'highlight-menu-item cursor-pointer rounded-md border-none bg-none opacity-50 hover:opacity-100' +
                                        (messageRating === 0 ? ' bg-slate-400/10 text-white/90 opacity-100' : '')
                                    }
                                    onClick={() => (message.id ? handleRate(message.id, 0) : null)}
                                    title='Not helpful'
                                >
                                    <svg width='20' height='20' viewBox='0 0 20 20' fill='currentColor'>
                                        <path d='M9.08466 18.1601L8.70578 18.1123L8.52508 18.0849C6.73664 17.758 5.58927 15.9828 6.02509 14.2178L6.07391 14.041L6.60131 12.3281C6.22917 12.3243 5.90391 12.3161 5.62077 12.2939C5.12631 12.2551 4.68908 12.1724 4.29849 11.9668L4.14722 11.8818C3.40344 11.4365 2.87218 10.7037 2.68329 9.85254L2.65296 9.69041C2.59699 9.31003 2.63528 8.91681 2.71653 8.49019C2.76254 8.24831 2.82654 7.97883 2.90404 7.67672L3.17449 6.66786L3.55443 5.27242C3.79245 4.39994 3.95623 3.76437 4.26339 3.26951L4.40502 3.06538C4.70106 2.68203 5.08029 2.37199 5.51337 2.15916L5.70181 2.07415C6.31153 1.82255 7.02148 1.83489 8.05536 1.83489H12.6667C13.3558 1.83489 13.9127 1.83431 14.363 1.87106C14.7636 1.90384 15.1244 1.96899 15.4606 2.11334L15.6032 2.17966C16.1242 2.44513 16.5608 2.84897 16.8649 3.34467L16.987 3.56338C17.1788 3.94007 17.2582 4.34593 17.2956 4.80369C17.3324 5.25394 17.3317 5.81094 17.3317 6.50004V8.33337C17.3317 9.06547 17.3379 9.56254 17.2243 9.98634L17.1628 10.1875C16.824 11.1823 16.01 11.9492 14.986 12.2236L14.8229 12.2607C14.4341 12.3357 13.974 12.332 13.3337 12.332C13.0571 12.332 12.8011 12.4796 12.6638 12.7197L9.74376 17.8301L9.68823 17.9121C9.54564 18.0926 9.31758 18.1891 9.08466 18.1601ZM12.6686 5.83304C12.6686 5.01597 12.6629 4.73734 12.6061 4.52544L12.57 4.40825C12.3736 3.83142 11.902 3.38667 11.3083 3.22754L11.1296 3.19334C10.9235 3.16774 10.6131 3.16504 9.99972 3.16504H8.05536C6.90103 3.16504 6.51619 3.17724 6.20972 3.30374L6.10033 3.35354C5.84923 3.47694 5.62842 3.65564 5.45669 3.87794L5.38449 3.98344C5.22306 4.24484 5.10319 4.64834 4.83759 5.62204L4.45669 7.01764L4.19209 8.00784C4.11959 8.28984 4.06319 8.52894 4.02309 8.73924C3.96359 9.05204 3.94829 9.26254 3.96059 9.42094L3.98209 9.56444C4.08439 10.025 4.35899 10.4262 4.74579 10.6875L4.91859 10.79C5.07469 10.8722 5.30179 10.9346 5.72529 10.9678C6.15229 11.0012 6.70769 11.0019 7.49972 11.0019C7.71072 11.0019 7.90949 11.1018 8.03489 11.2715C8.16019 11.4412 8.19749 11.6606 8.13539 11.8623L7.34439 14.4326L7.29459 14.6367C7.10589 15.6405 7.78399 16.6204 8.81219 16.7822L11.5095 12.0605C11.7697 11.6052 12.1857 11.2715 12.6686 11.1103V5.83304ZM16.0017 6.50004C16.0017 5.78894 16.0008 5.29564 15.9694 4.91214C15.9387 4.53644 15.882 4.32504 15.8015 4.16704L15.7311 4.04104C15.5548 3.75344 15.302 3.51924 14.9997 3.36524L14.8698 3.30954C14.726 3.25814 14.5366 3.22034 14.2546 3.19724C14.0223 3.17834 13.7496 3.17204 13.4128 3.16894C13.5875 3.41504 13.7293 3.68644 13.8288 3.97854L13.8903 4.18064C14.0039 4.60444 13.9987 5.10104 13.9987 5.83304V10.997C14.2061 10.9933 14.3499 10.9866 14.4626 10.9726L14.6413 10.9394C15.235 10.7804 15.7075 10.3355 15.904 9.75884L15.9392 9.64164C15.996 9.42954 16.0017 9.15074 16.0017 8.33337V6.50004Z' />
                                    </svg>
                                </Button>

                                <Button
                                    className='highlight-menu-item cursor-pointer rounded-md border-none bg-none opacity-50 hover:opacity-100'
                                    onClick={() => handleResubmit()}
                                    title='Resubmit'
                                >
                                    <svg width='20' height='20' viewBox='0 0 24 24' fill='none'>
                                        <path
                                            d='M3 12a9 9 0 019-9 9.75 9.75 0 016.74 2.74L21 8'
                                            stroke='currentColor'
                                            strokeWidth='2'
                                            strokeLinecap='round'
                                            strokeLinejoin='round'
                                        />
                                        <path
                                            d='M21 3v5h-5'
                                            stroke='currentColor'
                                            strokeWidth='2'
                                            strokeLinecap='round'
                                            strokeLinejoin='round'
                                        />
                                        <path
                                            d='M21 12a9 9 0 01-9 9 9.75 9.75 0 01-6.74-2.74L3 16'
                                            stroke='currentColor'
                                            strokeWidth='2'
                                            strokeLinecap='round'
                                            strokeLinejoin='round'
                                        />
                                        <path
                                            d='M3 21v-5h5'
                                            stroke='currentColor'
                                            strokeWidth='2'
                                            strokeLinecap='round'
                                            strokeLinejoin='round'
                                        />
                                    </svg>
                                </Button>
                                <Button
                                    className={`highlight-menu-item cursor-pointer rounded-md border-none bg-none opacity-50 transition-opacity hover:opacity-100 ${copiedMessageIndex === message.id ? 'pointer-events-none cursor-default text-green-400 opacity-100' : ''}`}
                                    onClick={() =>
                                        message.id ? copyMessage(message.content, message.id, handleCopy) : null
                                    }
                                    title={copiedMessageIndex === message.id ? 'Copied!' : 'Copy message'}
                                >
                                    {copiedMessageIndex === message.id ? (
                                        <svg width='20' height='20' viewBox='0 0 24 24' fill='none'>
                                            <path
                                                d='M20 6L9 17l-5-5'
                                                stroke='currentColor'
                                                strokeWidth='2'
                                                strokeLinecap='round'
                                                strokeLinejoin='round'
                                            />
                                        </svg>
                                    ) : (
                                        <svg width='20' height='20' viewBox='0 0 24 24' fill='none'>
                                            <path
                                                d='M8 4v12a2 2 0 002 2h8a2 2 0 002-2V7.242a2 2 0 00-.602-1.43L16.083 2.57A2 2 0 0014.685 2H10a2 2 0 00-2 2z'
                                                stroke='currentColor'
                                                strokeWidth='2'
                                                strokeLinecap='round'
                                                strokeLinejoin='round'
                                            />
                                            <path
                                                d='M16 18v2a2 2 0 01-2 2H6a2 2 0 01-2-2V9a2 2 0 012-2h2'
                                                stroke='currentColor'
                                                strokeWidth='2'
                                                strokeLinecap='round'
                                                strokeLinejoin='round'
                                            />
                                        </svg>
                                    )}
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
